"use client";

import Link from "next/link";
import { ArrowRight, Bell, Clock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ToolRegistrationCTAProps {
  title?: string;
  description?: string;
  features?: Array<{
    icon: React.ReactNode;
    text: string;
  }>;
}

const defaultFeatures = [
  {
    icon: <Clock className="size-4 text-primary" />,
    text: "Save screenshots and compare them anytime",
  },
  {
    icon: <Bell className="size-4 text-primary" />,
    text: "Get automated alerts when pages change",
  },
  {
    icon: <Zap className="size-4 text-primary" />,
    text: "Track multiple pages across different viewports",
  },
];

export function ToolRegistrationCTA({
  title = "Want to automate this?",
  description = "Create a free account to save your work and set up automated visual monitoring.",
  features = defaultFeatures,
}: ToolRegistrationCTAProps) {
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm">
              {feature.icon}
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild className="gap-2">
            <Link href="/register">
              Create free account
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href="/pricing">View pricing</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
